import { useState, useEffect } from "react";
import { api, formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { DEFAULTS, useContent } from "@/lib/content";
import { CONTENT_GROUPS, prettyLabel, isMultiline } from "./constants";

export default function ContentManager() {
  const { setContent } = useContent();
  const [fields, setFields] = useState(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    api.get("/content").then(({ data }) => setFields({ ...DEFAULTS, ...data }));
  }, []);

  const set = (k, v) => setFields((f) => ({ ...f, [k]: v }));

  const save = async () => {
    setSaving(true);
    try {
      const { data } = await api.put("/admin/content", { updates: fields });
      setFields({ ...DEFAULTS, ...data });
      setContent && setContent({ ...DEFAULTS, ...data });
      toast.success("Content saved — live on the site");
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Save failed");
    } finally {
      setSaving(false);
    }
  };

  const reset = async () => {
    if (!window.confirm("Reset ALL site text to the original defaults?")) return;
    try {
      const { data } = await api.post("/admin/content/reset");
      setFields({ ...DEFAULTS, ...data });
      setContent && setContent({ ...DEFAULTS, ...data });
      toast.success("Content reset to defaults");
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Reset failed");
    }
  };

  if (!fields) {
    return <p className="opacity-60 py-12 text-center animate-pulse" data-testid="content-loading">Loading content...</p>;
  }

  const inputCls = "w-full bg-transparent border-2 border-[#F2EFEB] px-3 py-2 focus:outline-none focus:border-[#DFFF00] text-sm";
  const f = fields;

  return (
    <div data-testid="content-panel">
      <div className="flex items-center justify-between flex-wrap gap-3 mb-6 sticky top-0 bg-[#090909] py-4 z-20 border-b-2 border-[#F2EFEB]">
        <p className="overline text-[#DFFF00]">Edit text · preview updates live · then save to publish</p>
        <div className="flex gap-3">
          <button onClick={reset} data-testid="content-reset-btn" className="border-2 border-[#FF2A00] text-[#FF2A00] px-4 py-2 text-sm hover:bg-[#FF2A00] hover:text-[#090909] transition-colors">Reset defaults</button>
          <button onClick={save} disabled={saving} data-testid="content-save-btn" className="border-2 border-[#F2EFEB] bg-[#F2EFEB] text-[#090909] px-6 py-2 text-sm uppercase tracking-widest hover:bg-[#DFFF00] hover:border-[#DFFF00] transition-colors disabled:opacity-60">{saving ? "..." : "Save changes"}</button>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
        {/* Editor */}
        <div data-testid="content-editor">
          {CONTENT_GROUPS.map((group) => {
            const keys = Object.keys(fields).filter((k) => group.prefixes.includes(k.split("_")[0]));
            if (!keys.length) return null;
            return (
              <div key={group.label} className="mb-10">
                <h3 className="font-display text-3xl mb-4 text-[#DFFF00]">{group.label}</h3>
                <div className="space-y-4">
                  {keys.map((k) => (
                    <div key={k}>
                      <label className="overline block mb-1 opacity-70">{prettyLabel(k)}</label>
                      {isMultiline(k, fields[k]) ? (
                        <textarea
                          value={fields[k]}
                          onChange={(e) => set(k, e.target.value)}
                          data-testid={`content-field-${k}`}
                          className={`${inputCls} min-h-[70px]`}
                        />
                      ) : (
                        <input
                          value={fields[k]}
                          onChange={(e) => set(k, e.target.value)}
                          data-testid={`content-field-${k}`}
                          className={inputCls}
                        />
                      )}
                    </div>
                  ))}
                </div>
              </div>
            );
          })}

        </div>

        {/* Live preview */}
        <div className="hidden lg:block">
          <div className="sticky top-24 border-2 border-[#DFFF00] max-h-[80vh] overflow-y-auto no-scrollbar" data-testid="content-preview">
            <div className="bg-[#DFFF00] text-[#090909] px-4 py-2 font-mono-ui text-xs uppercase tracking-widest sticky top-0 z-10">Live Preview</div>

            {/* Hero */}
            <div className="bg-[#090909] text-[#F2EFEB] p-6">
              <div className="flex justify-between overline text-[0.55rem] mb-4 opacity-70">
                <span>{f.hero_ticker_left}</span><span>{f.hero_ticker_right}</span>
              </div>
              <div className="font-display text-5xl leading-[0.82] tracking-tight">{f.hero_word1}</div>
              <div className="font-display text-5xl leading-[0.82] tracking-tight">{f.hero_word2}</div>
              <div className="font-serif-ed italic text-lg text-[#DFFF00] mt-2">{f.hero_subtitle}</div>
              <div className="inline-block mt-4 overline text-[0.55rem] border-2 border-[#F2EFEB] px-3 py-2">{f.hero_cta}</div>
            </div>

            {/* Manifesto label + collections */}
            <div className="bg-[#F2EFEB] text-[#090909] p-6 border-t-2 border-[#090909]">
              <p className="overline text-[#FF2A00] text-[0.55rem]">{f.manifesto_label}</p>
              <p className="font-serif-ed italic text-xl mt-3">{f.manifesto_ch1_title}</p>
            </div>

            {/* Collection section sample */}
            <div className="bg-[#F2EFEB] text-[#090909] p-6 border-t-2 border-[#090909]">
              <p className="overline text-[#FF2A00] text-[0.55rem]">01 · {f.col_originals_tagline}</p>
              <h4 className="font-display text-4xl leading-[0.85] tracking-tight mt-2">{f.col_originals_title}</h4>
              <p className="font-serif-ed italic text-sm mt-2 opacity-80">{f.col_originals_desc}</p>
            </div>

            {/* Signup */}
            <div className="bg-[#DFFF00] text-[#090909] p-6 border-t-2 border-[#090909]">
              <p className="overline text-[0.55rem]">{f.signup_label}</p>
              <div className="font-display text-4xl leading-[0.82] tracking-tight mt-2">
                {f.signup_heading.split("\n").map((l, i) => <span key={i} className="block">{l}</span>)}
              </div>
              <p className="font-serif-ed italic text-sm mt-3">{f.signup_body}</p>
              <div className="inline-block mt-4 border-2 border-[#090909] bg-[#090909] text-[#F2EFEB] px-4 py-2 font-mono-ui text-[0.6rem] uppercase tracking-widest">{f.signup_button}</div>
            </div>

            {/* Footer */}
            <div className="bg-[#090909] text-[#F2EFEB] p-6 border-t-2 border-[#F2EFEB]">
              <div className="font-display text-3xl leading-none">GOOD GRIEF<span className="text-[#DFFF00]">.</span></div>
              <p className="overline text-[0.55rem] mt-3 opacity-60">{f.footer_tagline}</p>
              <p className="overline text-[0.5rem] mt-4 opacity-40">{f.footer_rights}</p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
